import { type Level } from '@/features/domaine/moteur-regles/Level'
import { isDate, isNotNullUndefinedOrEmptyString, isNumber } from '@/features/shared/tools'
import { isSiret } from '@/features/shared/tools/luhn'
import { SAISIE_DATE_INVALIDE, SAISIE_EMAIL_INVALIDE, SAISIE_INVALIDE, SAISIE_OBLIGATOIRE } from './messages-erreurs'
import { preNotNullOrEmpty } from './preconditions'

export function rgEstMaxLengthFactory<TLevel extends Level>(maxLength: number, level?: TLevel) {
  return {
    'max-length': {
      pre: preNotNullOrEmpty,
      validateur: (value: any) => {
        return value.length <= maxLength
      },
      message: `Taille maximale autorisée ${maxLength} caractères`,
      level: level ?? 0, // Toujours technique
    },
  }
}

export function rgEstMinLengthFactory<TLevel extends Level>(minLength: number, level?: TLevel) {
  return {
    'min-length': {
      pre: preNotNullOrEmpty,
      validateur: (value: any) => {
        // console.log(value)
        return value.length >= minLength
      },
      message: `Taille minimum autorisée ${minLength} caractères`,
      level: level ?? 0, // Toujours technique
    },
  }
}

export function rgEstSiretFactory<TLevel extends Level>(level?: TLevel) {
  return {
    format: {
      pre: preNotNullOrEmpty,
      validateur: (value: any) => {
        return isSiret(value)
      },
      message: SAISIE_INVALIDE,
      level: level,
    },
  }
}

// Todo :  Régle basic à revoir
export function rgEstNumTelFactory<TLevel extends Level>(level?: TLevel, message?: string) {
  return rgTestRegExpFactory(/^(((\+[0-9]{2})[0-9 ]{7,16})|(0[0-9 ]{9,19}))$/, level, message)
}

export function rgTestRegExpFactory<TLevel extends Level>(regexp: string | RegExp, level?: TLevel, message?: string) {
  if (typeof regexp === 'string') regexp = new RegExp(regexp)
  return {
    format: {
      pre: preNotNullOrEmpty,
      validateur: (value: any) => regexp.test(value?.trim()),
      message: message ?? SAISIE_INVALIDE,
      level: level,
    },
  }
}

export function rgEstMailFactory<TLevel extends Level>(level?: TLevel, message?: string) {
  return rgTestRegExpFactory(
    /^[a-zA-Z0-9\-_éèàçâêîôûäëïöüù.+]+@[a-zA-Z0-9\-_éèàçâêîôûäëïöüù.]+\.[^@\s]+$/i,
    level,
    message ?? SAISIE_EMAIL_INVALIDE,
  )
}
//
export function rgEstNomOuPrenomFactory<TLevel extends Level>(level?: TLevel, message?: string) {
  return rgTestRegExpFactory(/^[a-z\s\-éèàçâêîôûäëïöüù']+$/i, level, message)
}

export function rgEstCodePostalFactory<TLevel extends Level>(level?: TLevel, message?: string) {
  return rgTestRegExpFactory(/^([0-9][0-9]|2[abAB])([0-9]{3})$/i, level, message)
}

export function rgEstVoieFactory<TLevel extends Level>(level?: TLevel, message?: string) {
  return rgTestRegExpFactory(
    /^([a-zA-Z0-9\u0080-\u024F]+(?:. |.|-| |'|,))*[a-zA-Z0-9\u0080-\u024F\d]+$/i,
    level,
    message,
  )
}

export function rgEstCommuneFactory<TLevel extends Level>(level?: TLevel, message?: string) {
  return rgTestRegExpFactory(/^([a-zA-Z\u0080-\u024F]+(?:.|-| |'|,))*[a-zA-Z\u0080-\u024F\d]+$/i, level, message)
}

export function rgEstObligatoireFactory<TLevel extends Level>(level?: TLevel, message?: string) {
  return {
    required: {
      validateur: isNotNullUndefinedOrEmptyString,
      message: message ?? SAISIE_OBLIGATOIRE,
      level: level,
    },
  }
}

export function rgEstNumericMinFactory<TLevel extends Level>(min: number, level?: TLevel, message?: string) {
  return {
    min: {
      pre: preNotNullOrEmpty,
      validateur: (value: any) => value > min,
      message: message ?? `La valeur doit être supérieure à ${min}`,
      level: level,
    },
  }
}

export function rgEstNumericMinOrEqualFactory<TLevel extends Level>(min: number, level?: TLevel, message?: string) {
  return {
    min: {
      pre: preNotNullOrEmpty,
      validateur: (value: any) => value >= min,
      message: message ?? `La valeur doit être supérieure ou égale à ${min}`,
      level: level,
    },
  }
}

export function rgEstNumericMaxFactory<TLevel extends Level>(max: number, level?: TLevel, message?: string) {
  return {
    max: {
      pre: preNotNullOrEmpty,
      validateur: (value: any) => value < max,
      message: message ?? `La valeur doit être inférieure à ${max}`,
      level: level,
    },
  }
}

export function rgEstCheckedFactory<TLevel extends Level>(level?: TLevel, message?: string) {
  return {
    checked: {
      pre: preNotNullOrEmpty,
      validateur: (value: any) => value === true,
      message: message ?? 'La case doit être cochée',
      level: level,
    },
  }
}

export const rgEstDate = {
  format: {
    pre: preNotNullOrEmpty,
    validateur: (value: any) => isDate(value),
    message: SAISIE_DATE_INVALIDE,
    level: 0, // Toujours technique
  },
} as const

export const rgEstNumeric = {
  format: {
    pre: preNotNullOrEmpty,
    validateur: (value: any) => isNumber(value),
    message: SAISIE_INVALIDE,
    level: 0, // Toujours technique
  },
} as const

export function rgEstVraiFactory<TLevel extends Level>(level?: TLevel, message?: string) {
  return {
    'est-vrai': {
      validateur: (value: any) => {
        return value === true
      },
      message: message ?? `L'option doit être sélectionnée`,
      level: level,
    },
  }
}
